import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  ArrowLeft,
  Send,
  MoreVertical,
  Phone,
  Video,
  Paperclip,
  Smile,
  Info,
  MessageCircle,
  Plus,
  Image
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';

// TypeScript interfaces
interface ChatParticipant {
  id: string;
  name: string;
  avatar?: string;
  sport: string;
  online: boolean;
}

interface LastMessage {
  content: string;
  timestamp: string;
  isRead: boolean;
  senderId: string;
}

interface Chat {
  id: string;
  participant: ChatParticipant;
  lastMessage: LastMessage | null;
  unreadCount: number;
}

interface Message {
  id: string;
  content: string;
  senderId: string;
  timestamp: string;
  isRead: boolean;
  type: string;
  status?: 'sending' | 'sent' | 'delivered' | 'read';
  reactions?: { [key: string]: string[] };
}

const ChatsPage: React.FC = () => {
  const { user } = useAuth();
  const { socket, isConnected, onlineUsers, typingUsers } = useSocket();
  const { chatId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (chatId) {
      loadChatDetail(chatId);
    } else {
      loadChats();
    }
  }, [chatId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Socket.IO real-time message handling
  useEffect(() => {
    if (!socket || !chatId) return;

    // Join the chat room
    socket.emit('join_chat', chatId);

    // Listen for new messages
    const handleNewMessage = (messageData: any) => {
      setMessages(prev => [...prev, messageData]);
      
      // Update chat list with new message
      setChats(prev => prev.map(chat => 
        chat.id === chatId 
          ? {
              ...chat,
              lastMessage: {
                content: messageData.content,
                timestamp: 'now',
                isRead: false,
                senderId: messageData.senderId
              },
              unreadCount: messageData.senderId !== 'me' ? (chat.unreadCount || 0) + 1 : chat.unreadCount
            }
          : chat
      ));
    };

    socket.on('new_message', handleNewMessage);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.emit('leave_chat', chatId);
    };
  }, [socket, chatId]);

  // Handle typing indicators
  useEffect(() => {
    if (!socket || !selectedChat || !user) return;

    const handleTyping = () => {
      if (!isTyping) {
        setIsTyping(true);
        socket.emit('typing_start', {
          chatId: selectedChat.id,
          userId: user._id,
          userName: `${user.firstName} ${user.lastName}`
        });
      }

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set new timeout to stop typing
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        socket.emit('typing_stop', {
          chatId: selectedChat.id,
          userId: user._id
        });
      }, 1000);
    };

    const messageInput = document.querySelector('input[type="text"]') as HTMLInputElement;
    if (messageInput) {
      messageInput.addEventListener('input', handleTyping);
      
      return () => {
        messageInput.removeEventListener('input', handleTyping);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      };
    }
  }, [socket, selectedChat, user, isTyping]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showEmojiPicker]);

  const loadChats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/chats');
      if (response.data.success) {
        setChats(response.data.data.chats);
      }
    } catch (error: any) {
      console.error('Failed to load chats:', error);
      // Only show error toast if we have no cached chats to display
      if (chats.length === 0) {
        toast.error('Failed to load chats');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadChatDetail = async (chatId: string) => {
    try {
      setLoading(true);
      
      const [chatRes, messagesRes] = await Promise.all([
        api.get(`/chats/${chatId}`),
        api.get(`/chats/${chatId}/messages`)
      ]);
      
      if (chatRes.data.success && messagesRes.data.success) {
        setSelectedChat(chatRes.data.data.chat);
        setMessages(messagesRes.data.data.messages);
        
        // Mark messages as read
        await api.put(`/chats/${chatId}/read`);
        
        // Update chat list to reflect read status
        setChats(prev => prev.map(c => 
          c.id === chatId ? { 
            ...c, 
            unreadCount: 0, 
            lastMessage: c.lastMessage ? { ...c.lastMessage, isRead: true } : null 
          } : c
        ));
      }
    } catch (error) {
      console.error('Failed to load chat detail:', error);
      toast.error('Failed to load chat');
      navigate('/chats');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChat || sendingMessage) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    setSendingMessage(true);

    try {
      // Optimistic update - add message immediately
      const tempMessage: Message = {
        id: `temp-${Date.now()}`,
        content: messageContent,
        senderId: 'me',
        timestamp: new Date().toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit' 
        }),
        isRead: false,
        type: 'text'
      };

      setMessages(prev => [...prev, tempMessage]);

      // Update chat list with new last message
      setChats(prev => prev.map(chat => 
        chat.id === selectedChat.id 
          ? {
              ...chat,
              lastMessage: {
                content: messageContent,
                timestamp: 'now',
                isRead: false,
                senderId: 'me'
              }
            }
          : chat
      ));

      // Send to API
      const response = await api.post(`/chats/${selectedChat.id}/messages`, {
        content: messageContent,
        type: 'text'
      });

      if (response.data.success) {
        // Replace temp message with real message
        const realMessage = response.data.data.message;
        setMessages(prev => prev.map(msg => 
          msg.id === tempMessage.id ? { ...realMessage, id: realMessage.id } : msg
        ));

        // Emit real-time message via Socket.IO
        if (socket) {
          socket.emit('send_message', {
            ...realMessage,
            chatId: selectedChat.id
          });
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
      
      // Remove the failed message
      setMessages(prev => prev.filter(msg => msg.id !== `temp-${Date.now()}`));
      
      // Restore the message in input
      setNewMessage(messageContent);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!selectedChat) return;

    try {
      setSendingMessage(true);
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'chats');
      formData.append('type', file.type.startsWith('image/') ? 'image' : 'video');

      // Upload file to server
      const uploadResponse = await api.post('/upload/single', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (uploadResponse.data.success) {
        const fileData = uploadResponse.data.data;
        
        // Send message with file attachment
        const messageResponse = await api.post(`/chats/${selectedChat.id}/messages`, {
          content: `📎 ${file.name}`,
          type: fileData.type,
          media: [{
            type: fileData.type,
            url: fileData.url,
            filename: file.name,
            size: fileData.size,
            thumbnail: fileData.thumbnail
          }]
        });
        
        if (messageResponse.data.success) {
          const newMessage = messageResponse.data.data.message;
          setMessages(prev => [...prev, newMessage]);
          toast.success('File shared successfully!');
          
          // Emit via Socket.IO
          if (socket) {
            socket.emit('send_message', {
              ...newMessage,
              chatId: selectedChat.id
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('Failed to upload file');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
    
    // Focus back on input
    const messageInput = document.querySelector('input[placeholder="Type a message..."]') as HTMLInputElement;
    if (messageInput) {
      messageInput.focus();
    }
  };

  const handleMessageReaction = async (messageId: string, reaction: string) => {
    try {
      // Send to API to persist reaction
      await api.post(`/chats/messages/${messageId}/reaction`, { reaction });
      
      // Update local state
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          const reactions = msg.reactions || {};
          if (!reactions[reaction]) {
            reactions[reaction] = [];
          }
          if (!reactions[reaction].includes(user?._id || '')) {
            reactions[reaction].push(user?._id || '');
          }
          return { ...msg, reactions };
        }
        return msg;
      }));
      
      // Emit reaction via Socket.IO for real-time update
      if (socket && selectedChat) {
        socket.emit('message_reaction', {
          chatId: selectedChat.id,
          messageId,
          reaction,
          userId: user?._id
        });
      }
    } catch (error) {
      console.error('Failed to add reaction:', error);
      toast.error('Failed to add reaction');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const createNewChat = async (participantId: string) => {
    try {
      const response = await api.post('/chats', { participantId });
      if (response.data.success) {
        const newChat = response.data.data.chat;
        setChats(prev => [newChat, ...prev]);
        navigate(`/chats/${newChat.id}`);
        setShowNewChatModal(false);
        toast.success('Chat created successfully!');
      }
    } catch (error) {
      console.error('Failed to create chat:', error);
      toast.error('Failed to create chat');
    }
  };

  const getSportBadgeColor = (sport: string) => {
    const colors = {
      'Chess': 'bg-purple-100 text-purple-800',
      'Cricket': 'bg-red-100 text-red-800',
      'Football': 'bg-blue-100 text-blue-800',
      'Tennis': 'bg-green-100 text-green-800'
    };
    return colors[sport as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const filteredChats = chats.filter(chat =>
    chat.participant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (chat.lastMessage && chat.lastMessage.content.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Chat List View
  if (!chatId) {
    return (
      <div className="max-w-4xl mx-auto bg-white min-h-[calc(100vh-120px)] sm:min-h-[calc(100vh-60px)]">
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h1 className="text-lg sm:text-xl font-bold text-gray-900">Chats</h1>
          <button 
            onClick={() => setShowNewChatModal(true)}
            className="p-2 text-blue-600 hover:text-blue-800"
          >
            <Plus className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-3 sm:p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 sm:pl-10 pr-4 py-2 text-sm sm:text-base bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Chat List */}
        <div className="divide-y divide-gray-200">
          {filteredChats.length > 0 ? (
            filteredChats.map((chat) => (
              <div
                key={chat.id}
                onClick={() => navigate(`/chats/${chat.id}`)}
                className="p-3 sm:p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center space-x-2 sm:space-x-3">
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs sm:text-sm font-medium">
                      {chat.participant.avatar ? (
                        <img
                          src={chat.participant.avatar}
                          alt={chat.participant.name}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        getInitials(chat.participant.name)
                      )}
                    </div>
                    {onlineUsers.has(chat.participant.id) && (
                      <div className="absolute bottom-0 right-0 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 border-2 border-white rounded-full"></div>
                    )}
                  </div>

                  {/* Chat Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <div className="flex items-center space-x-1 sm:space-x-2 min-w-0 flex-1">
                        <h3 className="font-semibold text-gray-900 truncate text-sm sm:text-base">
                          {chat.participant.name}
                        </h3>
                        <span className={`px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded-full ${getSportBadgeColor(chat.participant.sport)} whitespace-nowrap flex-shrink-0`}>
                          {chat.participant.sport}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {chat.lastMessage ? chat.lastMessage.timestamp : 'No messages'}
                        </span>
                        {chat.unreadCount > 0 && (
                          <div className="w-4 h-4 sm:w-5 sm:h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                            {chat.unreadCount}
                          </div>
                        )}
                      </div>
                    </div>
                    <p className={`text-xs sm:text-sm truncate ${
                      chat.lastMessage && chat.lastMessage.isRead ? 'text-gray-500' : 'text-gray-900 font-medium'
                    }`}>
                      {chat.lastMessage ? chat.lastMessage.content : 'Start a conversation...'}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 sm:py-12">
              <MessageCircle className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'No chats found' : 'No conversations yet'}
              </h3>
              <p className="text-sm sm:text-base text-gray-500 px-4">
                {searchTerm 
                  ? 'Try searching with different keywords' 
                  : 'Start a conversation with someone from your community'
                }
              </p>
            </div>
          )}
        </div>

        {/* New Chat Modal */}
        {showNewChatModal && (
          <NewChatModal 
            onClose={() => setShowNewChatModal(false)}
            onCreateChat={createNewChat}
          />
        )}
      </div>
    );
  }

  // Chat Detail View
  if (!selectedChat) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto bg-white h-[calc(100vh-120px)] sm:h-[calc(100vh-60px)] flex flex-col">
      {/* Chat Header */}
      <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
          <button
            onClick={() => navigate('/chats')}
            className="p-2 text-gray-600 hover:text-gray-800 flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          
          <div className="relative flex-shrink-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs sm:text-sm font-medium">
              {selectedChat.participant.avatar ? (
                <img
                  src={selectedChat.participant.avatar}
                  alt={selectedChat.participant.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                getInitials(selectedChat.participant.name)
              )}
            </div>
            {onlineUsers.has(selectedChat.participant.id) && (
              <div className="absolute bottom-0 right-0 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 border-2 border-white rounded-full"></div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{selectedChat.participant.name}</h2>
            <div className="flex items-center space-x-1 sm:space-x-2 flex-wrap">
              <span className={`px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded-full ${getSportBadgeColor(selectedChat.participant.sport)} whitespace-nowrap`}>
                {selectedChat.participant.sport}
              </span>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {onlineUsers.has(selectedChat.participant.id) ? 'Online' : 'Offline'}
              </span>
              {!isConnected && (
                <span className="text-xs text-red-500">• Connecting...</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
          <button className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-800">
            <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <button className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-800">
            <Video className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <button className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-800">
            <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
        {messages.map((message) => (
          <MessageBubble 
            key={message.id} 
            message={message} 
            onReaction={(messageId, reaction) => handleMessageReaction(messageId, reaction)}
          />
        ))}
        
        {/* Typing Indicator */}
        {Array.from(typingUsers.values()).map(({ userId, userName }) => (
          <div key={userId} className="flex justify-start">
            <div className="max-w-[75%] sm:max-w-xs lg:max-w-md px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-gray-200 text-gray-900">
              <div className="flex items-center space-x-1">
                <span className="text-xs sm:text-sm text-gray-600">{userName} is typing</span>
                <div className="flex space-x-1">
                  <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-3 sm:p-4 border-t border-gray-200">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="relative">
            <button 
              onClick={() => {
                setShowAttachmentMenu(!showAttachmentMenu);
                setShowEmojiPicker(false);
              }}
              className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-800"
            >
              <Paperclip className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            
            {/* Attachment Menu */}
            {showAttachmentMenu && (
              <div className="absolute bottom-12 left-0 bg-white border border-gray-200 rounded-lg shadow-lg p-2 min-w-[140px] sm:min-w-[150px] z-10">
                <button
                  onClick={() => {
                    fileInputRef.current?.click();
                    setShowAttachmentMenu(false);
                  }}
                  className="w-full text-left px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-gray-50 rounded-lg flex items-center space-x-2"
                >
                  <Paperclip className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="text-xs sm:text-sm">File</span>
                </button>
                <button
                  onClick={() => {
                    // TODO: Implement image upload
                    setShowAttachmentMenu(false);
                  }}
                  className="w-full text-left px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-gray-50 rounded-lg flex items-center space-x-2"
                >
                  <Image className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="text-xs sm:text-sm">Photo</span>
                </button>
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileUpload(file);
                }
              }}
            />
          </div>
          
          <div className="flex-1 flex items-center space-x-1 sm:space-x-2 bg-gray-100 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 relative">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 bg-transparent focus:outline-none text-sm sm:text-base"
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            />
            <div className="relative" ref={emojiPickerRef}>
              <button 
                onClick={() => {
                  setShowEmojiPicker(!showEmojiPicker);
                  setShowAttachmentMenu(false);
                }}
                className="p-1 text-gray-600 hover:text-gray-800"
              >
                <Smile className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              
              {/* Emoji Picker */}
              {showEmojiPicker && (
                <div className="absolute bottom-12 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-2 sm:p-3 z-10 w-56 sm:w-64 max-h-[50vh] overflow-y-auto">
                  {/* Popular Emojis */}
                  <div className="mb-2 sm:mb-3 pb-2 border-b border-gray-200">
                    <p className="text-xs text-gray-500 mb-1 sm:mb-2">Frequently Used</p>
                    <div className="flex flex-wrap gap-1">
                      {['😀', '😂', '❤️', '👍', '👎', '😢', '😡', '🔥'].map((emoji) => (
                        <button
                          key={emoji}
                          onClick={() => handleEmojiSelect(emoji)}
                          className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-gray-100 rounded text-base sm:text-lg transition-colors"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-7 sm:grid-cols-8 gap-1 sm:gap-2 max-h-40 sm:max-h-48 overflow-y-auto">
                    {[
                      '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣',
                      '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰',
                      '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜',
                      '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏',
                      '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣',
                      '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠',
                      '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨',
                      '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥',
                      '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧',
                      '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐',
                      '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑',
                      '🤠', '😈', '👿', '👹', '👺', '🤡', '💩', '👻',
                      '💀', '☠️', '👽', '👾', '🤖', '🎃', '😺', '😸',
                      '😹', '😻', '😼', '😽', '🙀', '😿', '😾', '👋',
                      '🤚', '🖐️', '✋', '🖖', '👌', '🤏', '✌️', '🤞',
                      '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇',
                      '☝️', '👍', '👎', '👊', '✊', '🤛', '🤜', '👏',
                      '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💅', '🤳',
                      '💪', '🦾', '🦿', '🦵', '🦶', '👂', '🦻', '👃',
                      '🧠', '🫀', '🫁', '🦷', '🦴', '👀', '👁️', '👅',
                      '👄', '💋', '🩸', '❤️', '🧡', '💛', '💚', '💙',
                      '💜', '🤎', '🖤', '🤍', '💔', '❣️', '💕', '💞',
                      '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️',
                      '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️'
                    ].map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => handleEmojiSelect(emoji)}
                        className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded text-lg transition-colors"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            className="p-1.5 sm:p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            <Send className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// New Chat Modal Component
interface NewChatModalProps {
  onClose: () => void;
  onCreateChat: (participantId: string) => void;
}

const NewChatModal: React.FC<NewChatModalProps> = ({ onClose, onCreateChat }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const searchUsers = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/search?q=${encodeURIComponent(query)}&filter=users`);
      if (response.data.success) {
        setSearchResults(response.data.data.users || []);
      }
    } catch (error) {
      console.error('Failed to search users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      searchUsers(searchTerm);
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-lg w-full sm:w-full sm:max-w-md max-h-[90vh] sm:max-h-96 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">New Chat</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl sm:text-xl"
            >
              ×
            </button>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 sm:pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {loading ? (
            <div className="flex justify-center py-6 sm:py-8">
              <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="space-y-2">
              {searchResults.map((user) => (
                <div
                  key={user.id}
                  onClick={() => onCreateChat(user.id)}
                  className="flex items-center space-x-2 sm:space-x-3 p-2 sm:p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                >
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs sm:text-sm font-medium flex-shrink-0">
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      getInitials(user.name)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">{user.name}</h4>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">{user.sport} • {user.role}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : searchTerm ? (
            <div className="text-center py-6 sm:py-8">
              <p className="text-sm sm:text-base text-gray-500">No users found</p>
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8">
              <MessageCircle className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-sm sm:text-base text-gray-500">Search for users to start a chat</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Message Bubble Component with Reactions
interface MessageBubbleProps {
  message: Message;
  onReaction: (messageId: string, reaction: string) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onReaction }) => {
  const [showReactions, setShowReactions] = useState(false);
  
  const reactions = ['👍', '❤️', '😂', '😮', '😢', '😡'];

  return (
    <div className={`flex ${message.senderId === 'me' ? 'justify-end' : 'justify-start'} group`}>
      <div className="relative">
        <div
          className={`max-w-[75%] sm:max-w-xs lg:max-w-md px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg ${
            message.senderId === 'me'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-900'
          }`}
          onDoubleClick={() => setShowReactions(!showReactions)}
        >
          <p className="text-xs sm:text-sm break-words">{message.content}</p>
          <div className={`flex items-center justify-between mt-1 ${
            message.senderId === 'me' ? 'text-blue-100' : 'text-gray-500'
          }`}>
            <p className="text-xs">{message.timestamp}</p>
            {message.senderId === 'me' && (
              <div className="flex items-center space-x-1 ml-2">
                {message.status === 'sending' && <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-pulse"></div>}
                {message.status === 'sent' && <div className="text-xs">✓</div>}
                {message.status === 'delivered' && <div className="text-xs">✓✓</div>}
                {message.status === 'read' && <div className="text-xs text-blue-300">✓✓</div>}
              </div>
            )}
          </div>
        </div>

        {/* Reaction Button */}
        <button
          onClick={() => setShowReactions(!showReactions)}
          className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 w-5 h-5 sm:w-6 sm:h-6 bg-white border border-gray-300 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
        >
          <Smile className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-500" />
        </button>

        {/* Reactions Popup */}
        {showReactions && (
          <div className="absolute top-0 right-0 transform -translate-y-full bg-white border border-gray-200 rounded-lg shadow-lg p-1.5 sm:p-2 flex space-x-1 z-10">
            {reactions.map((reaction) => (
              <button
                key={reaction}
                onClick={() => {
                  onReaction(message.id, reaction);
                  setShowReactions(false);
                }}
                className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors text-base sm:text-lg"
              >
                {reaction}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatsPage;